import type { AxiosError, InternalAxiosRequestConfig } from 'axios';
import axios from 'axios';
import { getAdminMode } from 'hooks/useAdminMode';
import { errorHandler } from 'utils/errorHandler';
import type { SetupParametersType } from '../accessToken';
import { refreshAccessToken, setAuthParams } from '../accessToken';

const axiosInterceptor = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL as string,
  headers: {
    'Content-Type': 'application/json',
  },
});

export const setupAxiosInterceptors = (params: SetupParametersType) => {
  setAuthParams(params);

  axiosInterceptor.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
      const controller = new AbortController();

      if (getAdminMode()) {
        config.headers['X-Admin-Mode'] = 'true';
      }

      const { token, scheme } = await refreshAccessToken(controller);
      if (token) {
        config.headers.Authorization = `${scheme} ${token}`;
      }

      return { ...config, signal: controller.signal };
    },
    (error: AxiosError) => {
      errorHandler(error);
      return Promise.reject(error);
    },
  );

  axiosInterceptor.interceptors.response.use(
    response => response,
    (error: AxiosError) => {
      if (axios.isCancel(error)) {
        return Promise.reject(error);
      }

      console.error(
        'Request failed:',
        JSON.stringify({
          message: error.message,
          code: error.code,
          status: error.response?.status,
          url: error.config?.url,
          method: error.config?.method,
        }),
      );
      return Promise.reject(error);
    },
  );
};

export default axiosInterceptor;
